import { BaseProjectile } from '../common/base_projectile.js';
import { createExplosion } from './explosion.js';

export function drawBananaSprite(ctx, x, y, radius = 7, spinAngle = 0, isBaby = false) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(spinAngle);

  const L = radius * 2.2;
  const T = radius * 0.95;

  if (isBaby) {
    ctx.shadowColor = 'rgba(250, 204, 21, 0.65)';
    ctx.shadowBlur = 6;
  }

  // 1. Banana Body Outer Outline / Fill
  const grad = ctx.createLinearGradient(0, -T, 0, T);
  grad.addColorStop(0, '#fef08a'); // Light yellow top highlight
  grad.addColorStop(0.4, '#facc15'); // Rich bright yellow
  grad.addColorStop(1, '#ca8a04'); // Warm golden amber shadow underside

  ctx.fillStyle = grad;
  ctx.strokeStyle = '#854d0e';
  ctx.lineWidth = isBaby ? 0.8 : 1.2;

  ctx.beginPath();
  // Bottom tail end
  ctx.moveTo(-L * 0.48, T * 0.25);
  // Outer belly curve
  ctx.quadraticCurveTo(0, T * 1.35, L * 0.48, -T * 0.28);
  // Inner back curve
  ctx.quadraticCurveTo(0, -T * 0.22, -L * 0.48, T * 0.25);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // 2. Longitudinal Peel Ridge / Facet Line
  ctx.strokeStyle = '#eab308';
  ctx.lineWidth = isBaby ? 0.6 : 0.9;
  ctx.beginPath();
  ctx.moveTo(-L * 0.44, T * 0.23);
  ctx.quadraticCurveTo(0, T * 0.65, L * 0.44, -T * 0.25);
  ctx.stroke();

  // 3. Specular shine on the outer curve
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.65)';
  ctx.lineWidth = isBaby ? 0.8 : 1.2;
  ctx.beginPath();
  ctx.moveTo(-L * 0.3, T * 0.8);
  ctx.quadraticCurveTo(0, T * 1.12, L * 0.28, T * 0.35);
  ctx.stroke();

  // 4. Dark stem at head (top-right)
  ctx.fillStyle = '#4d7c0f'; // Green base
  ctx.beginPath();
  ctx.moveTo(L * 0.44, -T * 0.22);
  ctx.lineTo(L * 0.54, -T * 0.38);
  ctx.lineTo(L * 0.50, -T * 0.52);
  ctx.lineTo(L * 0.40, -T * 0.36);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#451a03'; // Brown tip
  ctx.beginPath();
  ctx.arc(L * 0.52, -T * 0.44, isBaby ? 1.0 : 1.5, 0, Math.PI * 2);
  ctx.fill();

  // 5. Blossom end tip (bottom-left)
  ctx.fillStyle = '#451a03';
  ctx.beginPath();
  ctx.arc(-L * 0.47, T * 0.25, isBaby ? 0.9 : 1.3, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

export class Projectile extends BaseProjectile {
  constructor(x, y, vx, vy, type, game) {
    super(x, y, vx, vy, type, game);
  }

  // ─── Hook Implementations ─────────────────────────────────────────────────────

  playAudio(name) {
    this.game.audio.play(name);
  }

  doExplode() {
    createExplosion(this.x, this.y, this.blastRadius, this.maxDamage, this.knockbackForce, this.game);
    if (this.type === 'banana' || this.type === 'banana_shrapnel') {
      const count = this.type === 'banana' ? 14 : 8;
      this.game.particles.spawnBurst(this.x, this.y, 'banana_debris', count);
      if (this.game.camera) {
        this.game.camera.shake = Math.max(this.game.camera.shake || 0, this.type === 'banana' ? 16 : 10);
      }
    }
  }

  createShrapnel(x, y, vx, vy, type = 'cluster_shrapnel') {
    return new Projectile(x, y, vx, vy, type, this.game);
  }

  onWaterHit() {
    this.game.particles.spawnBurst(this.x, this.game.waterLevel, 'water', 8);
  }

  onFlightParticle(dt) {
    if (this.type === 'bazooka' || this.type === 'airstrike_missile' || this.type === 'super_sheep' || this.type === 'homing_missile') {
      if (Math.random() < 0.6 * dt) {
        this.game.particles.spawnBurst(this.x - this.vx * 0.5, this.y - this.vy * 0.5, 'smoke_trail', 1);
      }
    } else if (this.type === 'banana' || this.type === 'banana_shrapnel') {
      const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
      if (speed > 1.2 && Math.random() < 0.45 * dt) {
        this.game.particles.spawnBurst(this.x, this.y, 'banana_sparkle', 1);
      }
    }
  }

  // ─── Rendering ───────────────────────────────────────────────────────────────

  draw(ctx) {
    ctx.save();

    if (this.type === 'bazooka' || this.type === 'airstrike_missile') {
      ctx.translate(this.x, this.y);
      ctx.rotate(Math.atan2(this.vy, this.vx));

      ctx.fillStyle = '#dc2626'; // red nose cone
      ctx.beginPath();
      ctx.moveTo(8, 0); ctx.lineTo(2, -3); ctx.lineTo(2, 3);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#f1f5f9'; // white body
      ctx.fillRect(-6, -2.5, 8, 5);

      ctx.fillStyle = '#475569'; // grey tail fins
      ctx.fillRect(-8, -3.5, 2, 7);
    } else if (this.type === 'grenade' || this.type === 'cluster' || this.type === 'cluster_shrapnel') {
      ctx.fillStyle   = this.type === 'cluster_shrapnel' ? '#f59e0b' : '#166534';
      ctx.strokeStyle = '#f1f5f9';
      ctx.lineWidth   = 1;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    } else if (this.type === 'banana' || this.type === 'banana_shrapnel') {
      const isBaby = this.type === 'banana_shrapnel';
      const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
      const spinSpeed = 0.08 + speed * 0.025;
      const spin = (this.x * 0.08 + performance.now() * 0.007 * (this.vx >= 0 ? 1 : -1)) % (Math.PI * 2);
      drawBananaSprite(ctx, this.x, this.y, this.radius, spin, isBaby);
    } else if (this.type === 'super_sheep') {
      ctx.save();
      ctx.translate(this.x, this.y);
      ctx.rotate(Math.atan2(this.vy, this.vx));

      ctx.fillStyle = '#ef4444'; // Red cape
      ctx.beginPath();
      ctx.moveTo(-6, 0);
      ctx.lineTo(-14, -6);
      ctx.lineTo(-11, 6);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(-3, 4); ctx.lineTo(-3, 7);
      ctx.moveTo(3, 4); ctx.lineTo(3, 7);
      ctx.stroke();

      ctx.fillStyle = '#ffffff'; // fluffy body
      ctx.beginPath();
      ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(-3, -2, 3.5, 0, Math.PI * 2);
      ctx.arc(3, 1, 3.5, 0, Math.PI * 2);
      ctx.arc(-2, 2, 3, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#fca5a5'; // face
      ctx.beginPath();
      ctx.arc(this.radius - 1, -1, 3.5, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#000000';
      ctx.beginPath();
      ctx.arc(this.radius, -2, 0.7, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    } else if (this.type === 'holy') {
      ctx.fillStyle   = '#fbbf24';
      ctx.strokeStyle = '#78350f';
      ctx.lineWidth   = 1.5;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Cross
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth   = 1.5;
      ctx.beginPath();
      ctx.moveTo(this.x, this.y - this.radius);
      ctx.lineTo(this.x, this.y - this.radius - 5);
      ctx.moveTo(this.x - 3, this.y - this.radius - 3);
      ctx.lineTo(this.x + 3, this.y - this.radius - 3);
      ctx.stroke();
    } else if (this.type === 'homing_missile') {
      ctx.translate(this.x, this.y);
      ctx.rotate(Math.atan2(this.vy, this.vx));

      // Pulsing target-lock ring
      const pulse = 0.5 + 0.5 * Math.sin(performance.now() * 0.012);
      ctx.strokeStyle = `rgba(255, ${Math.round(50 + 180 * pulse)}, 0, ${0.5 + 0.5 * pulse})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(0, 0, 10 + pulse * 4, 0, Math.PI * 2);
      ctx.stroke();

      // Nose cone — hot orange
      ctx.fillStyle = '#f97316';
      ctx.beginPath();
      ctx.moveTo(10, 0); ctx.lineTo(4, -3); ctx.lineTo(4, 3);
      ctx.closePath();
      ctx.fill();

      // Body — teal
      ctx.fillStyle = '#0891b2';
      ctx.fillRect(-7, -2.5, 11, 5);

      // Fins — dark slate
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(-9, -4, 2.5, 8);

      // Thruster glow
      const glow = 0.6 + 0.4 * Math.sin(performance.now() * 0.02);
      ctx.fillStyle = `rgba(251, 191, 36, ${glow})`;
      ctx.beginPath();
      ctx.arc(-9, 0, 3 * glow, 0, Math.PI * 2);
      ctx.fill();
    } else if (this.type === 'dynamite') {
      ctx.fillStyle   = '#dc2626';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth   = 1;
      ctx.fillRect(this.x - 3, this.y - 5, 6, 10);
      ctx.strokeRect(this.x - 3, this.y - 5, 6, 10);

      // Sparking fuse
      const time = performance.now() * 0.05;
      ctx.strokeStyle = time % 2 < 1 ? '#fbbf24' : '#ef4444';
      ctx.lineWidth   = 1.5;
      ctx.beginPath();
      ctx.moveTo(this.x, this.y - 5);
      ctx.quadraticCurveTo(this.x + 3, this.y - 9, this.x + 4, this.y - 8);
      ctx.stroke();
    }

    // Fuse countdown timer badge above timed weapons & super_sheep
    if ((!this.contactFuse || this.type === 'super_sheep') && this.type !== 'banana_shrapnel' && this.type !== 'cluster_shrapnel' && this.fuse > 0) {
      const secs = Math.ceil(this.fuse);
      const isBanana = this.type === 'banana';
      ctx.font = '800 10px Space Grotesk';
      ctx.textAlign = 'center';
      ctx.fillStyle = isBanana ? 'rgba(30, 27, 75, 0.85)' : 'rgba(15, 23, 42, 0.75)';
      ctx.fillRect(this.x - 8, this.y - this.radius - 17, 16, 12);
      ctx.fillStyle = isBanana ? '#facc15' : '#f59e0b';
      ctx.fillText(`${secs}`, this.x, this.y - this.radius - 7);
    }

    ctx.restore();
  }
}
